import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import SaveIcon from "@mui/icons-material/Save";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { commonFieldStyle, formControlStyle } from "../FilterPanelStyles";
import FilterTemplateSaveDialog from "./FilterTemplateSaveDialog";
import { FilterTemplate } from "../types/FilterTemplateTypes";

type DialogMode = "save" | "rename" | null;

export interface FilterTemplateSelectorProps {
  templates: FilterTemplate[];
  activeTemplateId: string | null;
  onTemplateSelect: (templateId: string) => void;
  onSaveTemplate: (name: string) => Promise<void>;
  onUpdateTemplate: (templateId: string) => Promise<void>;
  onRenameTemplate: (templateId: string, name: string) => Promise<void>;
  onDeleteTemplate: (templateId: string) => Promise<void>;
  onClearSelection: () => void;
}

const FilterTemplateSelector = ({
  templates,
  activeTemplateId,
  onTemplateSelect,
  onSaveTemplate,
  onUpdateTemplate,
  onRenameTemplate,
  onDeleteTemplate,
  onClearSelection,
}: FilterTemplateSelectorProps) => {
  const theme = useTheme();
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [deleteTarget, setDeleteTarget] = useState<FilterTemplate | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === activeTemplateId) ?? null,
    [activeTemplateId, templates],
  );

  const closeDialog = () => {
    setDialogMode(null);
  };

  const handleSaveConfirm = async (name: string) => {
    await onSaveTemplate(name);
    closeDialog();
  };

  const handleRenameConfirm = async (name: string) => {
    if (!selectedTemplate) {
      return;
    }

    await onRenameTemplate(selectedTemplate.id, name);
    closeDialog();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }

    await onDeleteTemplate(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 0 }}
          options={templates}
          value={selectedTemplate}
          onChange={(_, value) => {
            if (value) {
              onTemplateSelect(value.id);
            } else {
              onClearSelection();
            }
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Filter Template"
              placeholder="Select template"
              sx={{
                ...formControlStyle(theme),
                "& .MuiOutlinedInput-root": {
                  ...commonFieldStyle(theme),
                },
              }}
            />
          )}
        />

        <Box sx={{ display: "flex", flexShrink: 0 }}>
          <Tooltip title="Save current filters as template" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => setDialogMode("save")}
              sx={{ color: theme.palette.primary.main }}
              aria-label="Save current filters as template"
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {selectedTemplate && (
            <>
              <Tooltip title="Overwrite selected template" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => void onUpdateTemplate(selectedTemplate.id)}
                  aria-label="Overwrite selected template"
                >
                  <BookmarkBorderIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Rename selected template" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => setDialogMode("rename")}
                  aria-label="Rename selected template"
                >
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete selected template" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => setDeleteTarget(selectedTemplate)}
                  aria-label="Delete selected template"
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <FilterTemplateSaveDialog
        open={dialogMode === "save"}
        title="Save Filter Template"
        confirmLabel="Save"
        onClose={closeDialog}
        onConfirm={handleSaveConfirm}
      />

      <FilterTemplateSaveDialog
        open={dialogMode === "rename"}
        title="Rename Filter Template"
        initialName={selectedTemplate?.name ?? ""}
        confirmLabel="Rename"
        onClose={closeDialog}
        onConfirm={handleRenameConfirm}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Filter Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete template &quot;{deleteTarget?.name}&quot;? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDeleteConfirm()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilterTemplateSelector;
